import { Box, Flex, Image, Link, Text, useToast } from '@chakra-ui/react';
import pencilSlashIcon from '../../../assets/vectors/pencilSlashIcon.svg';
import { MeContextState, Entity } from '../../../store/types/account';
import BuilderTabs from './BuilderTabs';
import TemplateDetails from './TemplateDetails';
import FrontFields from './FrontFields';
import BackFields from './BackFields';
import Features from './Features';
import Design from './Design';
import EnhancedDesignFeature from './EnhancedDesignFeature';
import FeaturedActionsFields from './FeaturedActionsFields';
import { allowedDesignsForTemplateType } from './utils/enhancedDesignEligibility';
import {
  formatTemplatePayload,
  stripAppleEnhancedDesignIfDisabled,
} from './utils/formatTemplatePayload';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import {
  useCreatePassTemplateMutation,
  useUpdatePassTemplateMutation,
  usePublishPassTemplateMutation,
} from '../../../store/redux/api/templateApi';
import { MeContext } from '../../../context/MeContext';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { dashboardMutation } from '../../../utils/dashboardMutation';
import {
  setNotificationHeaderDefault,
  setIsCreating,
  setFormCreateSuccessId,
  setFormWasDisabledOnLoad,
  setFocusedField,
  setAppleEnhancedDesignOnLoad,
} from '../../../store/redux/slices/template.slice';
import { templateSchema } from './validation/validationSchema';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NotificationsFeature from './NotificationsFeature';
import { useAllFieldsDuplicateValidation } from './validation/useTemplateFieldValidation';
import {
  hasNonNumericLoyaltyBalance,
  LOYALTY_BALANCE_ERROR_MESSAGE,
} from './utils/fieldHelpers';

import {
  setMultipleErrors,
  setError,
  clearError,
} from '../../../store/redux/slices/templateErrors.slice';
import {
  PassTemplateInput,
  CreatableTemplateStatus,
  UpdatePassTemplateRequest,
  EnhancedDesignType,
} from '../../../store/types/template';
import {
  SaveValidationResult,
  ValidationIssue,
  validateTemplateSave,
} from './utils/templateSaveValidation';
import TemplateSaveValidationModal from './modals/TemplateSaveValidationModal';
import { resolveSaveAction } from './utils/resolveSaveAction';
import {
  readSectionFocus,
  highlightSectionWhenReady,
  POSTER_RING_HIGHLIGHT,
  SECTION_PARAM,
  DEVICE_TAB_PARAM,
} from './utils/sectionFocus';
import ConfirmModal from '../../../common/ConfirmModal';
import PublishLimitReachedModal from './PublishLimitReachedModal';
import { useTemplateLimitReached } from '../../../hooks/useTemplateLimitReached';
import { validateImageDimensions } from './utils/imageHelpers';
import { useTemplateNameValidation } from './hooks/useTemplateNameValidation';
import { useValidateSlug } from './hooks/useValidateSlug';
import BannersSection from './stamp-cards/BannersSection';
import PushNotificationsSection from './stamp-cards/PushNotificationsSection';
import FieldUpdatesSection from './stamp-cards/FieldUpdatesSection';
import { useLocationBuilderNavigation } from './hooks/useLocationBuilderNavigation';
import {
  BUILDER_DEVICE_TAB,
  BUILDER_PASS_TAB,
  type BuilderDeviceTab,
  type BuilderPassTab,
} from './utils/templateBuilderTabs';
import { highlightElement } from '../../../hooks/useHighlightSection';
import {
  ENHANCED_GENERIC_SECTION_ID,
  ENHANCED_EVENT_TICKET_SECTION_ID,
} from './utils/appleEnhancedDesignConstants';

const ENHANCED_SECTION_LOCATION = {
  passTab: BUILDER_PASS_TAB.FRONT,
  deviceTab: BUILDER_DEVICE_TAB.APPLE,
  highlight: POSTER_RING_HIGHLIGHT,
};

// Sections that live behind specific builder tabs — we switch the user to the
// correct tab before scrolling so the target element actually exists in the DOM.
const SECTION_TAB_LOCATION: Record<
  string,
  {
    passTab?: BuilderPassTab;
    deviceTab?: BuilderDeviceTab;
    highlight?: typeof POSTER_RING_HIGHLIGHT;
  }
> = {
  'nfc-qr-section': {
    passTab: BUILDER_PASS_TAB.FEATURES,
    deviceTab: BUILDER_DEVICE_TAB.SHARED,
  },
  'design-section': { passTab: BUILDER_PASS_TAB.FRONT },
  'app-linking-section': { passTab: BUILDER_PASS_TAB.FEATURES },
  'locations-section': { passTab: BUILDER_PASS_TAB.FEATURES },
  'languages-section': {
    passTab: BUILDER_PASS_TAB.FEATURES,
    deviceTab: BUILDER_DEVICE_TAB.SHARED,
  },
  'beacons-section': {
    passTab: BUILDER_PASS_TAB.FEATURES,
    deviceTab: BUILDER_DEVICE_TAB.APPLE,
  },
  'fields-section': { passTab: BUILDER_PASS_TAB.BACK },
  [ENHANCED_GENERIC_SECTION_ID]: ENHANCED_SECTION_LOCATION,
  [ENHANCED_EVENT_TICKET_SECTION_ID]: ENHANCED_SECTION_LOCATION,
};

const ISSUE_DEVICE_TAB_MAP: Record<
  NonNullable<ValidationIssue['deviceTab']>,
  BuilderDeviceTab
> = {
  apple: BUILDER_DEVICE_TAB.APPLE,
  google: BUILDER_DEVICE_TAB.GOOGLE,
  shared: BUILDER_DEVICE_TAB.SHARED,
};

const scrollToSectionWhenReady = (id: string, attempts = 20): (() => void) => {
  let cancelled = false;
  const tick = (n: number) => {
    if (cancelled) return;
    requestAnimationFrame(() => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else if (n > 0) {
        tick(n - 1);
      }
    });
  };
  tick(attempts);
  return () => {
    cancelled = true;
  };
};

type PassTabsProps = {
  passTabs: BuilderPassTab[];
  selectedPassTab: BuilderPassTab;
  setSelectedPassTab: Dispatch<SetStateAction<BuilderPassTab>>;
  selectedTab: BuilderDeviceTab;
  setSelectedTab: Dispatch<SetStateAction<BuilderDeviceTab>>;
  isEdit: boolean;
  skipNoFormFieldsCheckRef?: React.MutableRefObject<boolean>;
  onShowNoFormFieldsModal?: () => void;
  onOpenInspirationModal?: () => void;
  pendingExpandLocationIndex?: number | null;
  setPendingExpandLocationIndex?: Dispatch<SetStateAction<number | null>>;
  onPendingExpandLocationConsumed?: () => void;
  saveIntentRef?: React.MutableRefObject<'draft' | 'publish' | 'save'>;
  publishBlockedOpen?: boolean;
  onPublishBlockedOpenChange?: (open: boolean) => void;
  googleRevertedPreview?: boolean;
  setGoogleRevertedPreview?: Dispatch<SetStateAction<boolean>>;
  isGooglePreviewActive?: boolean;
  onSaveCompleteRef?: React.MutableRefObject<(() => void) | null>;
};

const BuilderPanel = ({
  passTabs,
  selectedPassTab,
  setSelectedPassTab,
  selectedTab,
  setSelectedTab,
  isEdit,
  skipNoFormFieldsCheckRef,
  onShowNoFormFieldsModal,
  onOpenInspirationModal,
  pendingExpandLocationIndex,
  setPendingExpandLocationIndex,
  onPendingExpandLocationConsumed,
  saveIntentRef,
  publishBlockedOpen: publishBlockedOpenProp,
  onPublishBlockedOpenChange,
  googleRevertedPreview,
  setGoogleRevertedPreview,
  isGooglePreviewActive,
  onSaveCompleteRef,
}: PassTabsProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const meState = meStateRaw;
  const { isLimitReached } = useTemplateLimitReached();
  const [internalPublishBlockedOpen, setInternalPublishBlockedOpen] =
    useState(false);
  const publishBlockedOpen =
    publishBlockedOpenProp ?? internalPublishBlockedOpen;
  const setPublishBlockedOpen =
    onPublishBlockedOpenChange ?? setInternalPublishBlockedOpen;
  const filterEntity: Entity | null =
    meState.entities?.length === 1
      ? meState.entities[0]
      : (meState.filterEntity ?? null);

  const watermarkEnabled = meState.watermarkEnabled;
  const expressivePassRolloutEnabled =
    meState?.expressivePassRolloutEnabled === true;
  const localisationEnabled = meState?.localisationEnabled;
  const showGoogleClassicMessage =
    googleRevertedPreview &&
    (selectedTab === BUILDER_DEVICE_TAB.GOOGLE ||
      (selectedTab === BUILDER_DEVICE_TAB.SHARED && isGooglePreviewActive));
  const state = useAppSelector((state) => state.template);
  const templateErrors = useAppSelector((state) => state.templateErrors);
  const formWasDisabledOnLoad = useAppSelector(
    (s) => s.template._formWasDisabledOnLoad
  );
  const formState = useAppSelector((state) => state.form);

  const stateForPayload = {
    ...state,
    dynamicForm: { ...formState },
  };
  const [createPassTemplate] = useCreatePassTemplateMutation();
  const [updatePassTemplate] = useUpdatePassTemplateMutation();
  const [publishPassTemplate] = usePublishPassTemplateMutation();

  const dispatch = useAppDispatch();

  // Save-time validation modal (entitlement errors + unchanged preset data
  // warnings). Errors block save; warnings allow "Save anyway".
  const [saveValidation, setSaveValidation] =
    useState<SaveValidationResult | null>(null);

  const [validationMode, setValidationMode] = useState<'draft' | 'publish'>(
    'publish'
  );
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const pendingPayloadRef = useRef<PassTemplateInput | null>(null);
  const pendingStatusRef = useRef<CreatableTemplateStatus | undefined>(
    undefined
  );
  /**
   * Holds the section to scroll to once the modal's close animation + focus
   * restoration have finished. We can't scroll synchronously on click because
   * Chakra's `returnFocusOnClose` re-focuses the Save button and the browser
   * auto-scrolls to it, undoing our scroll.
   */
  const pendingSectionScrollRef = useRef<string | null>(null);

  const { isTemplateNameUnique } = useTemplateNameValidation(
    isEdit,
    state.uuid,
    filterEntity?.uuid
  );
  const { validateSlug } = useValidateSlug(state.uuid);

  const stampFeedbackTypes = useAppSelector(
    (s) => s.template.stampFeedbackTypes
  );

  useAllFieldsDuplicateValidation();

  // Validate loyalty balance default value across tab switches
  const loyaltyBalanceEnabled = useAppSelector(
    (s) => s.template.loyaltyBalance.enabled
  );
  const headerOneDefaultValue = useAppSelector(
    (s) => s.template.fields.header?.one?.defaultValue
  );
  useEffect(() => {
    if (
      hasNonNumericLoyaltyBalance(loyaltyBalanceEnabled, headerOneDefaultValue)
    ) {
      dispatch(
        setError({
          path: 'loyaltyBalance.defaultValue',
          message: LOYALTY_BALANCE_ERROR_MESSAGE,
        })
      );
    } else {
      dispatch(clearError('loyaltyBalance.defaultValue'));
    }
  }, [loyaltyBalanceEnabled, headerOneDefaultValue, dispatch]);

  const { navigateToSharedGeofencing, navigateToAppleLocationNotifications } =
    useLocationBuilderNavigation(setSelectedTab, setSelectedPassTab);

  // Reset to Front tab when switching to a device tab that doesn't have Back
  useEffect(() => {
    if (
      selectedTab !== BUILDER_DEVICE_TAB.SHARED &&
      selectedPassTab === BUILDER_PASS_TAB.BACK
    ) {
      setSelectedPassTab(BUILDER_PASS_TAB.FRONT);
    }
  }, [selectedTab, selectedPassTab, setSelectedPassTab]);

  const [cameFromEnhancedTicket, setCameFromEnhancedTicket] = useState(false);
  const [closedEnhancedDesigns, setClosedEnhancedDesigns] = useState<
    EnhancedDesignType[]
  >([]);

  useEffect(() => {
    if (
      selectedTab !== BUILDER_DEVICE_TAB.SHARED ||
      selectedPassTab !== BUILDER_PASS_TAB.FEATURES
    ) {
      setCameFromEnhancedTicket(false);
    }
  }, [selectedTab, selectedPassTab]);

  const cancelScrollRef = useRef<(() => void) | null>(null);
  const startScroll = useCallback((id: string) => {
    cancelScrollRef.current?.();
    cancelScrollRef.current = scrollToSectionWhenReady(id);
  }, []);
  useEffect(() => () => cancelScrollRef.current?.(), []);

  // Arriving from the validation modal on the template list: open the section
  // the issue pointed at, once the template has loaded and rendered it.
  const [pendingSectionFocus, setPendingSectionFocus] = useState<string | null>(
    null
  );

  useEffect(() => {
    const focus = readSectionFocus(searchParams);
    if (!focus) return;

    const target = SECTION_TAB_LOCATION[focus.sectionId];
    if (target?.passTab) setSelectedPassTab(target.passTab);
    const deviceTab = focus.deviceTab
      ? ISSUE_DEVICE_TAB_MAP[focus.deviceTab]
      : target?.deviceTab;
    if (deviceTab) setSelectedTab(deviceTab);
    setPendingSectionFocus(focus.sectionId);

    const next = new URLSearchParams(searchParams);
    next.delete(SECTION_PARAM);
    next.delete(DEVICE_TAB_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setSelectedPassTab, setSelectedTab]);

  useEffect(() => {
    if (!pendingSectionFocus) return;
    return highlightSectionWhenReady(
      pendingSectionFocus,
      () => setPendingSectionFocus(null),
      SECTION_TAB_LOCATION[pendingSectionFocus]?.highlight
    );
  }, [pendingSectionFocus]);

  const handleNavigateToNfc = useCallback(() => {
    setCameFromEnhancedTicket(true);
    setSelectedTab(BUILDER_DEVICE_TAB.SHARED);
    setSelectedPassTab(BUILDER_PASS_TAB.FEATURES);
    dispatch(setFocusedField('nfc.enabled'));
    startScroll('nfc-qr-section');
  }, [dispatch, setSelectedTab, setSelectedPassTab, startScroll]);

  const handleNavigateToEnhancedTicket = useCallback(() => {
    setCameFromEnhancedTicket(false);
    setSelectedTab(BUILDER_DEVICE_TAB.APPLE);
    setSelectedPassTab(BUILDER_PASS_TAB.FRONT);
    dispatch(setFocusedField('appleEnhancedDesign.eventTicket.enabled'));
    startScroll(ENHANCED_EVENT_TICKET_SECTION_ID);
  }, [dispatch, setSelectedTab, setSelectedPassTab, startScroll]);

  const displayNotificationsFeature = useMemo(() => {
    return (
      state.templateType !== 'stampCard' ||
      (state.templateType === 'stampCard' &&
        !state.stampFeedbackTypes?.includes('sendPushNotification'))
    );
  }, [state.templateType, state.stampFeedbackTypes]);

  useEffect(() => {
    if (!isEdit && filterEntity) {
      dispatch(setNotificationHeaderDefault({ entityName: filterEntity.name }));
    }
  }, [dispatch, isEdit, filterEntity]);

  const handleEdit = async () => {
    const passTemplate = formatTemplatePayload(
      stateForPayload,
      'update',
      localisationEnabled,
      true
    );
    stripAppleEnhancedDesignIfDisabled(
      passTemplate,
      meState.appleEnhancedDesignEnabled
    );
    return await updatePassTemplate({
      id: state.uuid,
      data: { passTemplate } as UpdatePassTemplateRequest,
      includeDynamicForm: true,
    }).unwrap();
  };

  // Runs the actual create/update — extracted so the save-validation modal's
  // "Save anyway" can call back into the same flow once the user confirms.
  const performSave = async (
    payload: PassTemplateInput,
    status?: CreatableTemplateStatus
  ) => {
    dispatch(setIsCreating(true));
    const isPublishing = status === 'published';

    let skipNavigation = false;

    await dashboardMutation(
      async () => {
        if (isEdit) {
          await handleEdit();
          if (isPublishing) {
            await publishPassTemplate({ id: state.uuid }).unwrap();
          }
          const formJustEnabled = formState.enabled && formWasDisabledOnLoad;
          if (formJustEnabled) {
            dispatch(setFormCreateSuccessId(state.uuid));
            dispatch(setFormWasDisabledOnLoad(false));
            dispatch(setIsCreating(false));
            skipNavigation = true;
            return;
          }
        } else {
          const result = await createPassTemplate({
            entityId: filterEntity?.uuid || undefined,
            presetId: state._presetId ?? undefined,
            passTemplate: status ? { ...payload, status } : payload,
          }).unwrap();

          const hasDynamicForm = formState.enabled === true;
          if (hasDynamicForm) {
            const newTemplateId =
              (result as { data?: { id: string }; id?: string }).data?.id ??
              (result as { id?: string }).id;
            if (newTemplateId) {
              dispatch(setFormCreateSuccessId(newTemplateId));
            }
            dispatch(setIsCreating(false));
            skipNavigation = true;
            return;
          }
        }
      },
      {
        action: isPublishing
          ? 'publishPassTemplate'
          : isEdit
            ? 'updatePassTemplate'
            : 'createPassTemplate',
        orgId: filterEntity?.uuid,
        toast,
        errorTitle: isPublishing
          ? 'Failed to publish template'
          : isEdit
            ? 'Failed to update template'
            : 'Failed to create template',
        onSuccess: () => {
          dispatch(
            setAppleEnhancedDesignOnLoad({
              type: state.appleEnhancedDesign.type,
              images: { ...state.appleEnhancedDesign.images },
              featuredActions: {
                one: { ...state.appleEnhancedDesign.featuredActions.one },
                two: { ...state.appleEnhancedDesign.featuredActions.two },
              },
            })
          );
          if (skipNavigation) return;
          dispatch(setIsCreating(false));
          if (onSaveCompleteRef?.current) {
            const cb = onSaveCompleteRef.current;
            onSaveCompleteRef.current = null;
            cb();
            return;
          }
          navigate('/pass-templates');
        },
        onError: (error) => {
          dispatch(setIsCreating(false));
          if (onSaveCompleteRef?.current) onSaveCompleteRef.current = null;

          if (isPublishing && (error as { status?: number })?.status === 402) {
            setPublishBlockedOpen(true);
            return true;
          }
        },
      }
    );
  };

  const handleConfirmSaveAnyway = async () => {
    const payload = pendingPayloadRef.current;
    if (!payload) {
      setSaveValidation(null);
      return;
    }
    const status = pendingStatusRef.current;
    pendingPayloadRef.current = null;
    pendingStatusRef.current = undefined;
    await performSave(payload, status);
    setSaveValidation(null);
  };

  const handleCloseValidationModal = () => {
    pendingPayloadRef.current = null;
    pendingStatusRef.current = undefined;
    setSaveValidation(null);
  };

  const handleConfirmPublish = async () => {
    const payload = pendingPayloadRef.current;
    if (!payload) {
      setPublishConfirmOpen(false);
      return;
    }
    pendingPayloadRef.current = null;
    pendingStatusRef.current = undefined;
    await performSave(payload, 'published');
    setPublishConfirmOpen(false);
  };

  const handleClosePublishConfirm = () => {
    pendingPayloadRef.current = null;
    pendingStatusRef.current = undefined;
    setPublishConfirmOpen(false);
  };

  const handleIssueClick = (issue: ValidationIssue) => {
    handleCloseValidationModal();
    if (!issue.sectionId) return;
    const target = SECTION_TAB_LOCATION[issue.sectionId];
    if (target?.passTab) setSelectedPassTab(target.passTab);
    const deviceTab = issue.deviceTab
      ? ISSUE_DEVICE_TAB_MAP[issue.deviceTab]
      : target?.deviceTab;
    if (deviceTab) setSelectedTab(deviceTab);
    window.setTimeout(() => {
      highlightElement(document.getElementById(issue.sectionId!), {
        block: 'start',
        offsetTop: 80,
      });
    }, 300);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const hasLinkedFormFields = formState.formFields.some(
      (f) => f.id && f.id !== 'none'
    );
    const isCreatingWithForm = !isEdit && formState.enabled;
    const isEnablingFormOnEdit =
      isEdit && formState.enabled && formWasDisabledOnLoad;
    const shouldShowNoFormFieldsModal =
      !hasLinkedFormFields && (isCreatingWithForm || isEnablingFormOnEdit);

    const payload = formatTemplatePayload(
      stateForPayload,
      'create',
      localisationEnabled,
      true
    );
    stripAppleEnhancedDesignIfDisabled(
      payload,
      meState.appleEnhancedDesignEnabled
    );

    const dimensionErrors = await validateImageDimensions(
      state.images,
      state.templateType,
      state.appleEnhancedDesign,
      expressivePassRolloutEnabled
    );

    const errorsWithDimensions = { ...dimensionErrors };

    // Block save if loyalty balance has a non-numeric default value
    if (
      hasNonNumericLoyaltyBalance(
        state.loyaltyBalance.enabled,
        state.fields.header?.one?.defaultValue
      )
    ) {
      dispatch(
        setError({
          path: 'loyaltyBalance.defaultValue',
          message: LOYALTY_BALANCE_ERROR_MESSAGE,
        })
      );
      dispatch(setMultipleErrors({ ...errorsWithDimensions }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const formForValidation = (() => {
        const slugTouched =
          formState.enabled && !formState.slug?.trim()
            ? true
            : formState.slugTouched;
        const formFields = formState.formFields.map((field) => {
          const base = {
            ...field,
            _idTouched: true,
            _labelTouched: true,
          };
          const validation =
            field.validation?.name === 'custom' && field.validation
              ? {
                  ...field.validation,
                  _patternTouched: true,
                  _messageTouched: true,
                }
              : field.validation;
          const options = field.options?.map((opt) => ({
            ...opt,
            _labelTouched: true,
          }));
          return {
            ...base,
            ...(validation !== undefined && { validation }),
            ...(options !== undefined && { options }),
          };
        });
        return {
          ...formState,
          slugTouched,
          formFields,
        };
      })();
      const stateWithForm = { ...state, form: formForValidation };
      await templateSchema.validate(stateWithForm, {
        abortEarly: false,
        context: {
          fields: state.fields,
          templateType: state.templateType,
          images: state.images,
          isTemplateNameUnique,
          validateSlug,
          autoGenerateFieldId: true,
          stampCount: state.stampCount,
          stampFeedbackTypes: state.stampFeedbackTypes,
        },
      });

      if (Object.keys(dimensionErrors).length > 0) {
        dispatch(setMultipleErrors(errorsWithDimensions));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (
        shouldShowNoFormFieldsModal &&
        onShowNoFormFieldsModal &&
        skipNoFormFieldsCheckRef &&
        !skipNoFormFieldsCheckRef.current
      ) {
        onShowNoFormFieldsModal();
        return;
      }
      if (skipNoFormFieldsCheckRef?.current) {
        skipNoFormFieldsCheckRef.current = false;
      }

      const validation = validateTemplateSave(state, meState);
      const hasIssues =
        validation.errors.length > 0 || validation.warnings.length > 0;

      const { action, status, validationMode } = resolveSaveAction({
        intent: saveIntentRef?.current ?? 'save',
        hasIssues,
        isLimitReached,
      });

      if (action === 'blocked') {
        setPublishBlockedOpen(true);
        return;
      }

      if (action === 'save') {
        await performSave(payload, status);
        return;
      }

      pendingPayloadRef.current = payload;
      pendingStatusRef.current = status;

      if (action === 'confirm') {
        setPublishConfirmOpen(true);
        return;
      }

      setValidationMode(validationMode);
      setSaveValidation(validation);
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'inner' in err &&
        Array.isArray(err.inner)
      ) {
        const sectionErrors: Record<string, string> = {};
        err.inner.forEach((e: { path?: string; message?: string }) => {
          if (e.path) {
            const normalisedPath = e.path.replace(/\[(\d+)\]/g, '.$1');
            sectionErrors[normalisedPath] = e.message || '';
          }
        });
        const allErrors = { ...dimensionErrors, ...sectionErrors };
        dispatch(setMultipleErrors(allErrors));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  return (
    <Box border="2px solid lightGrey" borderRadius="10px" p={8} h="auto">
      <form id="template-builder-form" onSubmit={handleSave} noValidate>
        <Flex>
          <BuilderTabs
            size="sm"
            tabs={
              selectedTab !== BUILDER_DEVICE_TAB.SHARED
                ? passTabs.filter((tab) => tab !== BUILDER_PASS_TAB.BACK)
                : passTabs
            }
            selectedTab={selectedPassTab}
            setSelectedTab={setSelectedPassTab}
            labelMap={
              expressivePassRolloutEnabled
                ? { [BUILDER_PASS_TAB.BACK]: 'Additional' }
                : undefined
            }
          />
        </Flex>
        {showGoogleClassicMessage && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            bg="#FFFFFF"
            border="1px solid"
            borderColor="grey"
            borderRadius="6px"
            px={6}
            py={8}
            gap={4}
            mt={1}
          >
            <Box
              border="3px solid"
              borderColor="#3A4A57"
              borderRadius="full"
              p={3}
            >
              <Image src={pencilSlashIcon} alt="" boxSize="22px" />
            </Box>
            <Text
              fontFamily="Inter SemiBold, sans-serif"
              fontSize="16px"
              color="inkBlue"
            >
              This layout cannot be edited
            </Text>
            <Text fontSize="16px" color="#666" lineHeight="1.4">
              Google generates this classic layout automatically for customers
              who switch back to it on their own device.
              <br />
              <br />
              Google expects to phase out this option later this year.
            </Text>
            <Link
              fontSize="16px"
              color="linkBlue"
              textDecoration="underline"
              textUnderlineOffset="4px"
              onClick={() => setGoogleRevertedPreview?.(false)}
            >
              Exit classic view →
            </Link>
          </Flex>
        )}
        {!showGoogleClassicMessage &&
          selectedPassTab === BUILDER_PASS_TAB.FRONT && (
            <Flex direction="column" gap={8} w="full">
              {selectedTab === BUILDER_DEVICE_TAB.SHARED && (
                <>
                  <TemplateDetails
                    watermarkEnabled={watermarkEnabled}
                    isEdit={isEdit}
                  />
                  {displayNotificationsFeature && <NotificationsFeature />}
                </>
              )}
              {selectedTab === BUILDER_DEVICE_TAB.APPLE &&
                allowedDesignsForTemplateType(state.templateType).map(
                  (design) => (
                    <EnhancedDesignFeature
                      key={design}
                      design={design}
                      onNavigateToNfc={handleNavigateToNfc}
                      isOpen={!closedEnhancedDesigns.includes(design)}
                      onClose={() =>
                        setClosedEnhancedDesigns((closed) => [
                          ...closed,
                          design,
                        ])
                      }
                    />
                  )
                )}
              <Design
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                watermarkEnabled={watermarkEnabled}
                expressivePassEnabled={expressivePassRolloutEnabled}
              />
              {selectedTab === BUILDER_DEVICE_TAB.APPLE && (
                <FeaturedActionsFields isEdit={isEdit} />
              )}
              {state.templateType === 'stampCard' &&
                stampFeedbackTypes?.includes('changePassBanner') &&
                selectedTab === BUILDER_DEVICE_TAB.SHARED && (
                  <BannersSection
                    watermarkEnabled={watermarkEnabled}
                    onOpenInspirationModal={onOpenInspirationModal}
                  />
                )}
              {selectedTab === BUILDER_DEVICE_TAB.SHARED &&
                state.templateType === 'stampCard' &&
                stampFeedbackTypes?.includes('sendPushNotification') && (
                  <PushNotificationsSection />
                )}
              {selectedTab === BUILDER_DEVICE_TAB.SHARED && (
                <FrontFields isEdit={isEdit} />
              )}
              {selectedTab === BUILDER_DEVICE_TAB.SHARED &&
                state.templateType === 'stampCard' &&
                stampFeedbackTypes?.includes('updatePassText') && (
                  <FieldUpdatesSection />
                )}
            </Flex>
          )}
        {!showGoogleClassicMessage &&
          selectedPassTab === BUILDER_PASS_TAB.BACK && (
            <Flex direction="column" gap={8} w="full">
              <BackFields
                isShared={selectedTab === BUILDER_DEVICE_TAB.SHARED}
                isEdit={isEdit}
              />
            </Flex>
          )}
        {!showGoogleClassicMessage &&
          selectedPassTab === BUILDER_PASS_TAB.FEATURES && (
            <Flex direction="column" gap={8} w="full">
              <Features
                selectedTab={selectedTab}
                onNavigateToSharedGeofencing={navigateToSharedGeofencing}
                onNavigateToAppleLocationNotifications={
                  navigateToAppleLocationNotifications
                }
                onNavigateToEnhancedTicket={handleNavigateToEnhancedTicket}
                cameFromEnhancedTicket={cameFromEnhancedTicket}
                pendingExpandLocationIndex={pendingExpandLocationIndex}
                onPendingExpandLocationConsumed={
                  onPendingExpandLocationConsumed
                }
                isEdit={isEdit}
              />
            </Flex>
          )}
      </form>
      <TemplateSaveValidationModal
        isOpen={saveValidation !== null}
        onClose={handleCloseValidationModal}
        onSaveAnyway={handleConfirmSaveAnyway}
        onIssueClick={handleIssueClick}
        result={saveValidation ?? { errors: [], warnings: [] }}
        isSaving={state._isCreating}
        mode={validationMode}
      />
      <ConfirmModal
        isOpen={publishConfirmOpen}
        onClose={handleClosePublishConfirm}
        title="Publish this template?"
        description={
          <>
            Once published, you can start issuing passes right away. Published
            templates{' '}
            <Box as="span" fontWeight="600">
              can't be deleted
            </Box>{' '}
            or reverted to draft.
          </>
        }
        primaryButton={{
          label: 'Publish template',
          onClick: handleConfirmPublish,
          isLoading: state._isCreating,
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: handleClosePublishConfirm,
        }}
      />
      <PublishLimitReachedModal
        isOpen={publishBlockedOpen}
        onClose={() => setPublishBlockedOpen(false)}
        onSaveAsDraft={() => {
          setPublishBlockedOpen(false);
          if (saveIntentRef) {
            saveIntentRef.current = 'draft';
          }
          (
            document.getElementById(
              'template-builder-form'
            ) as HTMLFormElement | null
          )?.requestSubmit();
        }}
      />
    </Box>
  );
};

export default BuilderPanel;
